// Admin-only client for the round/gateway control surface (docs/ROUND_CONTROL.md).
// Every call needs an admin Bearer token — see src/control/RoundControl.tsx
// for how that token is collected (prompted once, kept only in
// sessionStorage, never hardcoded here or anywhere in the bundle).

const BASE = (import.meta as unknown as { env: Record<string, string> }).env
  .VITE_AIM_API_BASE || "";

export class AdminApiError extends Error {
  status: number;
  detail: unknown;
  constructor(status: number, detail: unknown) {
    super(typeof detail === "string" ? detail : JSON.stringify(detail));
    this.status = status;
    this.detail = detail;
  }
}

async function adminFetch<T>(path: string, token: string, init?: RequestInit): Promise<T> {
  const res = await fetch(BASE + path, {
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: "application/json",
      ...(init?.body ? { "Content-Type": "application/json" } : {}),
      ...init?.headers,
    },
  });
  if (!res.ok) {
    let detail: unknown = res.statusText;
    try {
      detail = (await res.json()).detail ?? detail;
    } catch {}
    throw new AdminApiError(res.status, detail);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export type PositionSource = "CARRIER" | "GATEWAY_FUSION";
export type RoundStatus = "DRAFT" | "READY" | "LIVE" | "RECOVERED" | "CLOSED";

export interface RoundTargetConfig {
  targetId: string;
  enabledInRound: boolean;
  positionSource: PositionSource;
  carrierId: string | null;
  displayAlias: string | null;
  beaconMac: string | null;
  displayName: string | null;
}

export interface RoundInfo {
  roundId: string;
  displayName: string;
  status: RoundStatus;
  startedAt: string | null;
  recoveredAt: string | null;
  endedAt: string | null;
  createdAt: string;
  targets: RoundTargetConfig[];
}

export interface RoundTargetInput {
  targetId: string;
  enabledInRound?: boolean;
  positionSource?: PositionSource;
  carrierId?: string | null;
  displayAlias?: string | null;
}

export interface AdminTarget {
  targetId: string;
  beaconMac: string | null;
  displayName: string | null;
  enabled: boolean;
  status: string;
}

export interface GatewayInfo {
  observerId: string;
  enabled: boolean;
  zoneId: string | null;
  status: string | null;
  online: boolean;
  lastSeenAt: string | null;
  lastHeartbeatAt: string | null;
}

export const getCurrentRound = (token: string) => adminFetch<RoundInfo | null>("/api/admin/rounds/current", token);
export const getRound = (token: string, roundId: string) => adminFetch<RoundInfo>(`/api/admin/rounds/${encodeURIComponent(roundId)}`, token);
export const createRound = (token: string, displayName: string, targets: RoundTargetInput[]) =>
  adminFetch<RoundInfo>("/api/admin/rounds", token, { method: "POST", body: JSON.stringify({ displayName, targets }) });
export const updateRound = (token: string, roundId: string, body: { displayName?: string; targets?: RoundTargetInput[] }) =>
  adminFetch<RoundInfo>(`/api/admin/rounds/${encodeURIComponent(roundId)}`, token, { method: "PUT", body: JSON.stringify(body) });
export const readyRound = (token: string, roundId: string) =>
  adminFetch<RoundInfo>(`/api/admin/rounds/${encodeURIComponent(roundId)}/ready`, token, { method: "POST" });
export const startRound = (token: string, roundId: string) =>
  adminFetch<RoundInfo>(`/api/admin/rounds/${encodeURIComponent(roundId)}/start`, token, { method: "POST" });
export const closeRound = (token: string, roundId: string) =>
  adminFetch<RoundInfo>(`/api/admin/rounds/${encodeURIComponent(roundId)}/close`, token, { method: "POST" });
export const resetRound = (token: string, roundId: string) =>
  adminFetch<RoundInfo>(`/api/admin/rounds/${encodeURIComponent(roundId)}/reset`, token, { method: "POST" });

export const listAdminTargets = (token: string) => adminFetch<AdminTarget[]>("/api/admin/targets", token);
export const upsertAdminTarget = (token: string, targetId: string, body: { beaconMac?: string; displayName?: string; enabled?: boolean }) =>
  adminFetch<AdminTarget>(`/api/admin/targets/${encodeURIComponent(targetId)}`, token, { method: "PUT", body: JSON.stringify(body) });

export const listGateways = (token: string) => adminFetch<GatewayInfo[]>("/api/admin/gateways", token);
export const setGatewayEnabled = (token: string, observerId: string, enabled: boolean) =>
  adminFetch<GatewayInfo>(`/api/admin/gateways/${encodeURIComponent(observerId)}`, token, { method: "PUT", body: JSON.stringify({ enabled }) });

export const recoverTarget = (token: string, targetId: string, observerId: string) =>
  adminFetch<void>(`/api/targets/${encodeURIComponent(targetId)}/recovered`, token, {
    method: "POST",
    body: JSON.stringify({ targetId, observerId, confirmedAt: new Date().toISOString() }),
  });
